/**
 * Video script gate (status `script_review`): the shot plan as a Time | Visual | Spoken line table,
 * missing asset needs, revision history — nothing is generated (no spend) until a human approves.
 *
 * Data: creatives.get → creative.shotPlan {version, summary, structure, totalDurationSec, performer:{description},
 *   treatment:{captions, music, notes[]}, shots:[{index, start, end, visual, framing, motion, onScreenText,
 *   spokenLine, purpose, type, assetNeeds:[{kind, required, assetId, status}]}], missingAssets:[{shotIndex, kind, prompt}],
 *   revisions:[{at, by, notes}], approved}
 * Tools: creatives.revise_script {brandId, creativeId, notes (≥3)} → {version, plan, missingAssets}
 *        creatives.approve_script {brandId, creativeId, note?, allowMissingAssets} (HUMAN-ONLY) → {status:"draft", batchId, jobId}
 */
import { AlertTriangle, ArrowUpRight, Clapperboard, FilePenLine, PlayCircle } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { useToolMutation } from "@/api/hooks";
import { toast } from "@/api/toast-store";
import { cn } from "@/lib/cn";
import { humanize, relative } from "@/lib/format";
import { brandPath } from "@/state/brand";
import { Pill } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Checkbox, Textarea } from "../ui/Form";

interface Shot {
  index?: number;
  start: number;
  end: number;
  visual?: string;
  onScreenText?: string | null;
  spokenLine?: string | null;
  purpose?: string;
  type?: string;
  assetNeeds?: { kind: string; required?: boolean; status?: string }[];
}

interface ShotPlan {
  version?: number;
  summary?: string;
  structure?: string;
  totalDurationSec?: number;
  performer?: { description?: string };
  treatment?: { captions?: string; music?: boolean; notes?: string[] };
  shots?: Shot[];
  missingAssets?: { shotIndex: number; kind: string; prompt: string }[];
  revisions?: { at: string; by?: string; notes: string }[];
}

export function readShotPlan(raw: unknown): ShotPlan | null {
  if (!raw || typeof raw !== "object") return null;
  const p = raw as ShotPlan;
  return Array.isArray(p.shots) ? p : null;
}

export function ScriptReviewPanel({ brandId, creativeId, plan, className }: { brandId: string; creativeId: string; plan: ShotPlan; className?: string }) {
  const [notes, setNotes] = useState("");
  const [revising, setRevising] = useState(false);
  const [allowMissing, setAllowMissing] = useState(false);
  const missing = plan.missingAssets ?? [];
  const revise = useToolMutation("creatives.revise_script", {
    success: "Script revised — review the new version",
    onSuccess: () => {
      setNotes("");
      setRevising(false);
    },
  });
  const approve = useToolMutation<Record<string, unknown>>("creatives.approve_script", {
    label: "Approve script — start video generation",
    onSuccess: (r) => {
      const batchId = typeof r?.batchId === "string" ? r.batchId : null;
      toast({
        tone: "success",
        title: "Script approved — generating the video",
        body: "Shots render, get stitched with captions and go through QA before your final approval.",
        action: batchId ? { label: "Track in Studio", href: brandPath(brandId, `studio?batch=${encodeURIComponent(batchId)}`) } : undefined,
      });
    },
  });
  return (
    <section className={cn("rounded-card border border-info/30 bg-info-soft/20", className)} aria-label="Video script review">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[14px] font-semibold">
            <Clapperboard className="size-4 text-info" strokeWidth={1.75} />
            Script review{typeof plan.version === "number" ? ` · v${plan.version}` : ""}
            {typeof plan.totalDurationSec === "number" && <span className="font-normal text-muted">· {Math.round(plan.totalDurationSec)}s</span>}
          </div>
          <p className="mt-0.5 text-[12.5px] text-muted">The video is planned, not generated. Nothing is spent until you approve the script and scenes.</p>
        </div>
      </header>
      <div className="flex flex-col gap-4 p-4">
        {plan.summary && <p className="text-[13px] leading-relaxed text-fg/90">{plan.summary}</p>}
        {(plan.structure || plan.performer?.description) && (
          <div className="flex flex-wrap gap-1.5 text-[12px]">
            {plan.structure && <Pill tone="neutral">{plan.structure}</Pill>}
            {plan.performer?.description && <Pill tone="muted">Performer · {plan.performer.description}</Pill>}
            {plan.treatment?.captions === "lower_speech_captions" && <Pill tone="muted">Speech captions</Pill>}
            {plan.treatment?.music && <Pill tone="muted">Music</Pill>}
          </div>
        )}
        <div className="overflow-x-auto rounded-lg border border-line scrollbar-thin">
          <table className="w-full min-w-[560px] border-collapse text-left text-[12.5px]">
            <thead className="bg-surface-2 text-[11px] text-subtle">
              <tr>
                <th className="w-[76px] px-3 py-2 font-medium">Time</th>
                <th className="px-3 py-2 font-medium">Visual</th>
                <th className="px-3 py-2 font-medium">Spoken line</th>
              </tr>
            </thead>
            <tbody>
              {(plan.shots ?? []).map((s, i) => {
                const needs = (s.assetNeeds ?? []).filter((n) => n.status === "missing");
                return (
                  <tr key={i} className="border-t border-line align-top">
                    <td className="px-3 py-2 text-muted tnum">
                      {s.start}–{s.end}s
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-fg/90">{s.visual}</div>
                      {s.onScreenText && <div className="mt-1 text-[11.5px] text-lavender">On screen: “{s.onScreenText}”</div>}
                      <div className="mt-1 flex flex-wrap gap-1">
                        {s.type && <Pill size="xs">{humanize(s.type)}</Pill>}
                        {needs.map((n, j) => (
                          <Pill key={j} size="xs" tone="warning">
                            needs {humanize(n.kind)}
                          </Pill>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-fg/90">{s.spokenLine ? `“${s.spokenLine}”` : <span className="text-subtle">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {missing.length > 0 && (
          <div className="flex gap-2.5 rounded-xl border border-warning/35 bg-warning-soft/40 p-3 text-[12.5px]" role="alert">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" strokeWidth={1.75} />
            <div className="min-w-0 flex-1">
              <div className="font-medium text-fg">Assets needed before production</div>
              <ul className="mt-1 flex list-disc flex-col gap-0.5 pl-4 text-fg/85">
                {missing.map((m, i) => (
                  <li key={i}>
                    Shot {m.shotIndex + 1}: {m.prompt}
                  </li>
                ))}
              </ul>
              <Link to={brandPath(brandId, "assets")} className="mt-1.5 inline-flex items-center gap-1 text-link hover:text-lavender">
                Upload to Brand assets <ArrowUpRight className="size-3" />
              </Link>
            </div>
          </div>
        )}
        {(plan.revisions ?? []).length > 0 && (
          <details className="text-[12px] text-muted">
            <summary className="cursor-pointer select-none text-fg/80">
              {plan.revisions!.length} revision{plan.revisions!.length > 1 ? "s" : ""}
            </summary>
            <ul className="mt-1.5 flex flex-col gap-1">
              {plan.revisions!.map((r, i) => (
                <li key={i}>
                  <span className="text-subtle">{relative(r.at)}:</span> {r.notes}
                </li>
              ))}
            </ul>
          </details>
        )}
        {revising ? (
          <div className="flex flex-col gap-2">
            <Textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What should change? e.g. “Open on the problem, not the product. Make the performer 30s, casual kitchen.”"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setRevising(false)}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" disabled={notes.trim().length < 3} loading={revise.isPending} onClick={() => revise.mutate({ brandId, creativeId, notes: notes.trim() })}>
                Revise script
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {missing.length > 0 && (
              <Checkbox checked={allowMissing} onChange={setAllowMissing} label={<span className="text-[12px] text-muted">Generate missing inserts without a real UI</span>} className="mr-auto" />
            )}
            <Button size="sm" variant="secondary" icon={<FilePenLine className="size-3.5" />} onClick={() => setRevising(true)}>
              Request changes
            </Button>
            <Button
              size="sm"
              variant="primary"
              icon={<PlayCircle className="size-3.5" />}
              disabled={missing.length > 0 && !allowMissing}
              loading={approve.isPending}
              onClick={() => approve.mutate({ brandId, creativeId, allowMissingAssets: allowMissing })}
            >
              Approve script
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
